import styled, { css } from 'styled-components';
import { colors, media, remCalc, layout } from '@prisjakt/ui-base';
import Button from '@prisjakt/ui-button';
import Dropdown from '@prisjakt/ui-dropdown';
import QuickPriceFiltersComponent from './quick-price-filters/quick-price-filters';
import PriceFiltersComponent from './price-filters/price-filters';

import AdContainerComponent from '../../../../components/ads';

type AdvertProps = {
  withPosition?: boolean;
};

const compensateForPadding = remCalc(layout.gridColumnGutter.small.value / 2);

export const AdContainer = styled(AdContainerComponent)`
  @media screen and (max-width: 340px) {
    margin-left: -${compensateForPadding};
    margin-right: -${compensateForPadding};
  }
`;

export const PlaceholderRow = styled.div`
  border-bottom: 1px solid ${colors.lightGray1};
  display: flex;
  height: 64px;
  padding: 15px 0;
  width: 100%;
`;

const PlaceholderBox = styled.div`
  background: ${colors.lightGray1};
  border-radius: 3px;
  height: 100%;
`;

export const ShopPlaceholder = styled(PlaceholderBox)`
  margin-right: 10%;
  flex: 0 1 150px;
`;

export const StockAndPricePlaceholder = styled(PlaceholderBox)`
  margin-left: auto;
  flex: 0 1 150px;
`;

export const CallToActionPlaceholder = styled(PlaceholderBox)`
  flex: 0 0 25px;
  margin-left: 10%;
  ${media.largeUp`
    margin-right: 15px;
    flex: 0 0 130px;
  `};
`;

export const PriceRowWrapper = styled.ul`
  list-style: none;
  display: flex;
  flex-direction: column;
`;

export const PriceRowTable = styled.table`
  width: 100%;
  border-spacing: 0;
  table-layout: fixed;
  ${media.mediumDown`
    thead th > div {
      height: 0;
      visibility: hidden;
    }
  `}
`;

const sharedPriceRowTableHeadStyles = css`
  font-size: ${layout.measurements.fontSizeSmall};
  padding-top: ${layout.spacing.xSmall};
  padding-bottom: ${layout.spacing.xSmall};
  border-top: 1px solid ${p => p.theme.lines.primary};
  border-bottom: 1px solid ${p => p.theme.lines.primary};
`;

const sharedPaddingStyles = css`
  padding-left: 10px;
  padding-right: 10px;
`;

export const PriceRowTableHeadExpand = styled.th(
  props => css`
    width: 50px;

    ${media.largeUp`
      ${sharedPriceRowTableHeadStyles};
    `}

    ${media.smallOnly`
      width: 35px;
    `}
  `
);

export const PriceRowTableHeadStore = styled.th`
  width: 108px;

  ${media.largeUp`
      ${sharedPriceRowTableHeadStyles};
      ${sharedPaddingStyles};
      text-align: initial;
    `}
`;

export const PriceRowTableHeadProduct = styled.th(
  props => css`
    width: auto;
    ${media.largeUp`
      ${sharedPriceRowTableHeadStyles};
      ${sharedPaddingStyles};
      text-align: initial;
    `}
  `
);

export const PriceRowTableHeadStock = styled.th(
  props => css`
    width: 10%;

    ${media.largeUp`
      ${sharedPriceRowTableHeadStyles};
      text-align: center;
    `}
  `
);

export const PriceRowTableHeadPrice = styled.th(
  props => css`
    width: 18%;

    ${media.largeUp`
      ${sharedPriceRowTableHeadStyles};
      ${sharedPaddingStyles};
      text-align: end;
    `}

    ${media.mediumDown`
      width: 29%;
    `}
  `
);

export const PriceRowTableHeadClickOut = styled.th`
    width: 155px;
    
    ${media.largeUp`
      ${sharedPriceRowTableHeadStyles};
    `}

    ${media.mediumDown`
      width: 38px;
    `}
    ${media.smallOnly`
      width: 40px;
    `}
  `;

export const Information = styled.div`
  border-top: 1px solid ${colors.lightGray1};
  color: ${colors.darkGray2};
  font-size: ${remCalc(13)};
  font-style: italic;
  padding-top: ${remCalc(10)};
  padding-bottom: ${remCalc(10)};
`;

export const AdvertTableRowElement = styled.tr<AdvertProps>`
  width: 100%;
  border-bottom: none;

  ${p =>
    p.withPosition &&
    css`
      order: 3;
    `};
`;

export const NoPrices = styled.div`
  margin-top: ${remCalc(20)};
  border: none;

  div {
    margin-top: ${remCalc(10)};
  }

  a {
    margin-top: ${remCalc(10)};
    color: ${p => p.theme.text.callToAction};
  }
`;

export const QuickPriceFilters = styled(QuickPriceFiltersComponent)``;

export const NewPriceFilter = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

export const PriceFilters = styled(PriceFiltersComponent)`
  min-width: 200px;
`;

export const PriceFilterButton = styled(Button)`
  padding: 0;
  margin-left: ${layout.spacing.xSmall};

  ${media.mediumDown`
    margin-right: ${layout.spacing.xSmall};
  `};
`;

export const PriceFilterLabel = styled.span`
  font-weight: 100;
  color: ${p => p.theme.text.secondary};
`;

export const PriceFilter = styled(Dropdown)`
  /* Merge dropdown border with the divider when expanded */
  margin-left: -1px;

  &.ui-dropdown {
    &-expanded {
      .ui-dropdown--header {
        margin-bottom: -1px;
        border-bottom: 1px solid ${colors.white};
        /* Have to end up on top of the content */
        z-index: 2;
      }
    }

    .ui-dropdown--sub-content {
      padding-top: ${remCalc(10)};
      padding-bottom: ${remCalc(10)};
      width: 200px;
      right: 0;
      border-top: 1px solid ${colors.lightGray2};
    }

    /* On mobile, we only want the three vertical dots, not the arrow. */
    ${media.smallOnly`
        .pj-ui-icon--arrow {
          display: none;
        }
  `}
  }

  /* Style the dropdown toggle as 'selected'.
    Not a good way to achieve this, but necessary until
    we refactor the Dropdown component. */
  .ui-dropdown--toggle-button,
  .ui-dropdown--title-expanded {
    color: ${p => p.theme.text.callToAction};
    font-weight: 600;

    &:hover {
      color: ${p => p.theme.text.callToActionHover};

      .ui-dropdown--title-expanded {
        color: ${p => p.theme.text.callToActionHover};
      }

      svg path {
        fill: ${p => p.theme.icons.callToActionHover};
      }
    }
  }
`;

export const OnlyMobile = styled.span`
  display: none;

  ${media.smallOnly`
    display: inherit;
  `}
`;

export const TabletDesktop = styled.span`
  display: flex;
  align-items: center;

  ${media.smallOnly`
    display: none;
  `}
`;

export const OnlyDesktop = styled.span(
  props => css`
    display: flex;
    align-items: center;
    text-transform: none;

    ${media.mediumDown`
    display: none;
  `}
  `
);

export const TitleCount = styled.span`
  font-weight: 100;
  font-size: ${layout.measurements.fontSizeSmall};
  margin-left: ${layout.spacing.xxSmall};
  align-self: flex-end;
`;

export const Header = styled.div(
  props => css`
    ${media.largeUp`
      margin: ${layout.spacing.xSmall} 0;
    `}

    .pj-ui-panel-header--title {
      display: block;
    }

    .pj-ui-panel-header--child-container-only-children {
      justify-content: flex-end;
    }
  `
);
